import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, ButtonContainer, Checkbox, Layout, Modal, Spinner } from '@vwfs-bronson/bronson-react';
import { TFunction } from 'i18next';
import { Analytics } from '../../../services/analytics';
import { getPrintDocumentsEvent, getSendEmailDocumentsEvent } from '../../../services/analytics/analyticsEvents';
import {
  getDocumentTemplates,
  printQuoteDocuments,
  sendQuoteDocuments,
} from '../../../services/api/miles/miles-services';
import { MilesDocumentTemplatesResponse } from '../../../services/common/miles-pos-quote-integration/models';
import { MilesTemplate, Selectable } from '../../../services/common/miscUtils.types';

type TemplatesList = Array<Selectable<MilesTemplate>>;

const getInfoFromTemplatesList = (templatesList: TemplatesList) => {
  const selectedTemplates = templatesList.filter((item) => item.checked);
  const idsList = selectedTemplates.map(({ id }) => id);
  const namesString = selectedTemplates.map(({ name }) => name).join(' | ');

  return { idsList, namesString };
};

const openDocumentsLinks = (urlsList: string[]) => {
  const anchor = document.createElement('a');
  anchor.target = '_blank';
  document.body.appendChild(anchor);

  urlsList.forEach((url) => {
    anchor.href = url;
    anchor.click();
  });

  document.body.removeChild(anchor);
};

const getDocumentsLinks = async (templatesList: TemplatesList, quoteId: string | undefined) => {
  if (!quoteId || templatesList.length === 0) return undefined;

  const { idsList, namesString } = getInfoFromTemplatesList(templatesList);

  const response = await printQuoteDocuments(quoteId, idsList);

  Analytics.push(getPrintDocumentsEvent(namesString));

  return response.urls;
};

const onClickSendEmailButton = async (templatesList: TemplatesList, quoteId?: string) => {
  if (!quoteId || templatesList.length === 0) return;

  const { idsList, namesString } = getInfoFromTemplatesList(templatesList);

  await sendQuoteDocuments(quoteId, idsList);

  Analytics.push(getSendEmailDocumentsEvent(namesString));
};

const setAllDocumentsSelection = (documents: TemplatesList, checked: boolean) => {
  return documents.map((d) => ({ ...d, checked }));
};

const selectAllDocuments = (documents: TemplatesList) => setAllDocumentsSelection(documents, true);

const deselectAllDocuments = (documents: TemplatesList) => setAllDocumentsSelection(documents, false);

const toggleDocumentSelection = (documents: TemplatesList, id: string) => {
  return documents.map((d) => ({ ...d, checked: d.id === id ? !d.checked : d.checked }));
};

const areAllDocumentsChecked = (documents: TemplatesList) => {
  return documents.every((d) => d.checked);
};

const mapTemplatesResponseToSelectableTemplates = (response: MilesDocumentTemplatesResponse): TemplatesList => {
  return response.financialProduct.offers[0]?.documents?.map(({ template }) => {
    return {
      ...template,
      checked: false,
    };
  });
};

const renderLoadingTemplatesStatus = (t: TFunction) => (
  <Modal shown title="spinnerModal" hideTitle>
    <Layout className="u-pb">
      <Layout.Item>
        <Layout.Item>
          <span>{t('step3:loadingDocuments')}</span>
        </Layout.Item>
        <Spinner center />
      </Layout.Item>
    </Layout>
  </Modal>
);

const renderFailureInfoStatus = (t: TFunction, onConfirm: () => void) => (
  <Modal
    status="error"
    shown
    buttonConfirmText={t('step3:goBack')}
    onConfirm={onConfirm}
    title="failedToSendOrPrintDocument"
    hideTitle
  >
    {t('step3:failedToSendOrPrintDocument')}
  </Modal>
);

const renderInProgressStatus = (t: TFunction, isEmail: boolean) => (
  <Modal shown title="documentsInfoModal" hideTitle>
    <Layout className="u-pb">
      <Layout.Item>
        <span>{t(`step3:${isEmail ? 'sendingDocumentsInfo' : 'printingDocumentsInfo'}`)}</span>
      </Layout.Item>
      <Layout.Item>
        <Spinner center />
      </Layout.Item>
    </Layout>
  </Modal>
);

const renderPopupBlockingInfoModal = (t: TFunction, closeModal: () => void) => (
  <Modal
    shown
    buttonConfirmLabel={t('step3:navigation:return')}
    onClose={closeModal}
    title={t('app:popupBlockedInfoModalTitle')}
    hideTitle
  >
    <Layout className="u-pb">
      <Layout.Item>
        <div className="u-text-center">{t(`step3:popupsBlocked:label`)}</div>
      </Layout.Item>
      <Layout.Item>
        <ButtonContainer center>
          <Button type="button" onClick={closeModal}>
            {t('step3:navigation:return')}
          </Button>
        </ButtonContainer>
      </Layout.Item>
    </Layout>
  </Modal>
);

interface Props {
  t: TFunction;
  setOpenModal: (open: boolean) => void;
  shown: boolean;
  quoteId?: string;
}

export const DocumentsPrintAndEmailSendoutModal: FunctionComponent<Props> = ({ t, setOpenModal, shown, quoteId }) => {
  const [templatesList, setTemplatesList] = useState<TemplatesList>([]);
  const [disabledButtons, setDisabledButtons] = useState(true);
  const [sendingInProgress, setSendingInProgress] = useState(false);
  const [printingInProgress, setPrintingInProgress] = useState(false);
  const [showFailureInfo, setShowFailureInfo] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [generatedDocumentsLinks, setGeneratedDocumentsLinks] = useState<string[]>([]);
  const [canReuseGeneratedLinks, setCanReuseGeneratedLinks] = useState(false);
  const [showPopupsBlockingInfoModal, setShowPopupsBlockingInfoModal] = useState(false);

  const topItemRef = React.useRef<HTMLElement>(null);

  useEffect(() => {
    if (!topItemRef?.current) return;
    topItemRef.current.scrollIntoView(false);
  }, [shown]);

  useEffect(() => {
    if (!quoteId || !shown) return;

    setCanReuseGeneratedLinks(false);

    setLoadingTemplates(true);

    getDocumentTemplates(quoteId)
      .then(mapTemplatesResponseToSelectableTemplates)
      .then((templates) => {
        setTemplatesList(templates);
        setLoadingTemplates(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, shown]);

  useEffect(() => {
    const selectedDocuments = templatesList.filter((document) => document.checked);
    if (selectedDocuments.length) {
      setDisabledButtons(false);
    } else {
      setDisabledButtons(true);
    }
  }, [templatesList]);

  if (loadingTemplates) {
    return renderLoadingTemplatesStatus(t);
  }

  if (showFailureInfo) {
    return renderFailureInfoStatus(t, () => setShowFailureInfo(false));
  }

  if (sendingInProgress || printingInProgress) {
    return renderInProgressStatus(t, sendingInProgress);
  }

  if (showPopupsBlockingInfoModal) {
    return renderPopupBlockingInfoModal(t, () => setShowPopupsBlockingInfoModal(false));
  }

  return (
    <Modal
      onCancel={() => setOpenModal(false)}
      onClickOutside={() => setOpenModal(false)}
      onClose={() => setOpenModal(false)}
      shown={shown}
      title="doucmentsModal"
      hideTitle
    >
      <Layout className="u-pb">
        <Layout.Item>
          <Checkbox
            onChange={(event) => {
              setTemplatesList(event.target.checked ? selectAllDocuments : deselectAllDocuments);
              setCanReuseGeneratedLinks(false);
            }}
            checked={areAllDocumentsChecked(templatesList)}
          >
            <span ref={topItemRef}>{t('step3:Documents')}</span>
          </Checkbox>
        </Layout.Item>
        {templatesList.map(({ id, checked, name }, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Layout className="u-ml-large u-ml-small@s" key={index}>
            <br />
            <Layout.Item default="1/1">
              <Checkbox
                id={id}
                checked={checked}
                onChange={() => {
                  setTemplatesList((currentList) => toggleDocumentSelection(currentList, id));
                  setCanReuseGeneratedLinks(false);
                }}
              >
                <span>
                  <span className="documents">{name}</span>
                </span>
              </Checkbox>
            </Layout.Item>
          </Layout>
        ))}
      </Layout>

      <ButtonContainer center>
        <Button
          disabled={disabledButtons}
          type="button"
          onClick={() => {
            if (canReuseGeneratedLinks) {
              openDocumentsLinks(generatedDocumentsLinks);
              return;
            }

            setPrintingInProgress(true);
            getDocumentsLinks(templatesList, quoteId)
              .then((documentsLinks) => {
                if (!documentsLinks) return;
                setGeneratedDocumentsLinks(documentsLinks);
                setCanReuseGeneratedLinks(true);
                setShowPopupsBlockingInfoModal(true);
                openDocumentsLinks(documentsLinks);
              })
              .catch(() => {
                setShowFailureInfo(true);
              })
              .finally(() => {
                setPrintingInProgress(false);
              });
          }}
        >
          {t('step3:printConfirm:label')}
        </Button>
        <Button
          disabled={disabledButtons}
          onClick={() => {
            setSendingInProgress(true);
            onClickSendEmailButton(templatesList, quoteId)
              .catch(() => {
                setShowFailureInfo(true);
              })
              .finally(() => {
                setSendingInProgress(false);
              });
          }}
          type="button"
        >
          {t('step3:sendEmail:label')}
        </Button>
      </ButtonContainer>
    </Modal>
  );
};
